import { REGION } from '@common/consts';
import { isDefined } from '@vueuse/shared';
import type { InputHTMLAttributes } from 'vue';
import { format as dateFormat } from '@common/date';
import { DateTime } from 'luxon';
import Version from '@common/version';

const BASE_FLAG_CODE = 0x1f185;

/**
 * Converte un codice paese ISO in emoji bandiera usando i codepoint Unicode
 * @param cc - Codice paese ISO a 2 lettere (es. 'IT', 'FR', 'DE')
 * @returns Emoji bandiera corrispondente
 */
export function emojiflag(cc: string) {
  const codepoints: number[] = [];
  for (const c of cc.toLocaleLowerCase()) {
    codepoints.push(BASE_FLAG_CODE + c.charCodeAt(0));
  }
  return String.fromCodePoint(...codepoints);
}

/**
 * Crea una funzione di filtro che cerca la query in tutti i campi di un oggetto (case-insensitive)
 * @param q - Stringa di ricerca
 * @returns Funzione predicato che verifica se l'oggetto contiene la query in almeno un campo
 */
export function any(q: string) {
  return function (el: Record<string, any>) {
    if (!q) {
      return true;
    }
    q = q.toLocaleLowerCase();
    const keys = Object.keys(el);
    for (const k of keys) {
      if (!el[k]) {
        continue;
      }
      const subject = String(el[k]).toLocaleLowerCase();
      if (subject.includes(q)) {
        return true;
      }
    }
    return false;
  };
}

// cache collator for performance
const sortCollator = new Intl.Collator(undefined, {
  usage: 'sort',
  sensitivity: 'base',
});

/**
 * Crea una funzione di ordinamento per Array.sort() basata su campo o funzione
 * @template T - Tipo degli elementi da ordinare
 * @param f - Nome del campo o funzione estrattore del valore
 * @param reverse - Se true, inverte l'ordinamento (default: false)
 * @returns Funzione comparatore per Array.sort()
 */
export function by<T>(f: (i: T) => any, reverse?: boolean): (a: T, b: T) => number;
export function by<T extends Record<any, any>, F extends keyof T>(f: F, reverse?: boolean): (a: T, b: T) => number;
export function by(f: string | ((i: any) => any), reverse = false) {
  if (typeof f === 'function') {
    return byFn(f, reverse);
  }
  return byProp<any, string>(f, reverse);
}

export function defaultCompare(a: any, b: any): number {
  const aNull = typeof a === 'undefined' || a === null || (typeof a === 'number' && isNaN(a));
  const bNull = typeof b === 'undefined' || b === null || (typeof b === 'number' && isNaN(b));

  if (aNull && bNull) {
    return 0;
  }

  if (a instanceof Version && !(b instanceof Version)) {
    if (typeof b === 'string') {
      b = new Version(b);
    } else {
      a = a.toString();
    }
  }

  if (b instanceof Version && !(a instanceof Version)) {
    if (typeof a === 'string') {
      a = new Version(a);
    } else {
      b = b.toString();
    }
  }

  if (a instanceof Version && b instanceof Version) {
    return a.compare(b);
  }

  if (aNull) {
    return -1;
  }

  if (bNull) {
    return 1;
  }

  if (typeof a == 'string' || typeof b == 'string') {
    return sortCollator.compare(a, b);
  }

  if (a < b) {
    return -1;
  }

  if (a > b) {
    return 1;
  }

  return 0;
}

function byFn<T>(f: (i: T) => any, reverse = false) {
  const reverseFactor = reverse ? -1 : 1;
  return function (a: T, b: T) {
    if (!f) {
      return 0;
    }
    return defaultCompare(f(a), f(b)) * reverseFactor;
  };
}

function byProp<T extends object, F extends keyof T>(f: F, reverse = false) {
  return byFn((i: T) => i[f], reverse);
}

/**
 * Combina multiple funzioni di ordinamento in una singola, applicandole in sequenza
 * @param ff - Array di funzioni comparatore
 * @returns Funzione comparatore che applica tutti i comparatori in ordine fino al primo risultato non-zero
 */
export function multiby(...ff: ((a: any, b: any) => number)[]) {
  return function (a: any, b: any) {
    for (const f of ff) {
      const result = f(a, b);
      if (result !== 0) {
        return result;
      }
    }
    return 0;
  };
}

// cache formatters for performance
const numFormatter = new Intl.NumberFormat(undefined);
const intFormatter = new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 });
const dec2Formatter = new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 });
const currFormatters: Record<string, Intl.NumberFormat> = {};
const conjunctionFormatter = new Intl.ListFormat();
const disjunctionFormatter = new Intl.ListFormat(undefined, { type: 'disjunction' });

/**
 * Formatta un numero secondo le convenzioni locali del browser
 * @param value - Numero da formattare (può essere string, number, null o undefined)
 * @returns Stringa formattata, o stringa vuota se il valore non è definito
 */
export function formatNumber(value: number | string | null | undefined) {
  if (!isDefined(value)) {
    return '';
  }

  value = Number(value);
  let formatted = numFormatter.format(value);
  if (isNaN(value)) {
    formatted = formatted.replace('NaN', '-');
  }

  return formatted;
}

/**
 * Formatta un valore monetario con il simbolo della valuta specificata
 * @param value - Importo da formattare
 * @param currency - Codice ISO 4217 della valuta (es. 'EUR', 'USD', 'CHF')
 * @returns Stringa formattata con simbolo valuta, o stringa vuota se il valore non è definito
 */
export function formatCurrency(value: string | number | null | undefined, currency: string) {
  if (!isDefined(value)) {
    return '';
  }

  let fmt = currFormatters[currency];
  if (!fmt) {
    const options: Intl.NumberFormatOptions = {
      style: 'currency',
      currency,
    };
    fmt = new Intl.NumberFormat(undefined, options);
    currFormatters[currency] = fmt;
  }

  value = Number(value);
  let formatted = fmt.format(value);
  if (isNaN(value)) {
    formatted = formatted.replace('NaN', '-');
  }

  return formatted;
}

/**
 * Formatta una dimensione in byte con unità appropriate (KB, MB, GB, ecc.)
 * @param size - Dimensione in byte da formattare
 * @param base2 - Se true usa base 1024 (KiB, MiB), altrimenti base 1000 (KB, MB) (default: false)
 * @returns Stringa formattata con unità, o stringa vuota se non valido
 */
export function formatSize(size: string | number | null | undefined, base2 = false) {
  if (!isDefined(size)) {
    return '';
  }

  size = Number(size);
  if (isNaN(size)) {
    return '';
  }

  const base = base2 ? 1024 : 1000;
  const suffix = base2 ? 'iB' : 'B';

  const units = ['', 'K', 'M', 'G', 'T', 'P'];
  let unitX = 0;
  while (size >= base && unitX < units.length) {
    unitX++;
    size /= base;
  }
  const fmt = unitX < 2 ? intFormatter : dec2Formatter;
  return `${fmt.format(size)} ${units[unitX]}${suffix}`;
}

/** Formattatore per numeri decimali con massimo 2 cifre decimali */
export const formatDecimal2 = dec2Formatter.format;

/**
 * Wrapper per funzioni di formattazione che gestisce valori null/undefined/NaN restituendo stringa vuota
 * @template T - Tipo del valore da formattare
 * @template R - Tipi dei parametri aggiuntivi
 * @param f - Funzione di formattazione da wrappare
 * @returns Funzione wrapped che restituisce stringa vuota per valori non validi
 */
export function formatValidOnly<T, R extends unknown[]>(f: (v: T, ...rest: R) => string): (v: T | null | undefined, ...rest: R) => string {
  return (v, ...rest) => {
    try {
      return v === null || v === undefined || (typeof v !== 'string' && isNaN(v as any)) ? '' : f(v, ...rest);
    } catch (e) {
      if (e instanceof RangeError) {
        return '';
      }
      throw e;
    }
  };
}

/**
 * Formatta una lista di stringhe con separatori localizzati (es. 'a, b e c' o 'a, b o c')
 * @param values - Iterable di stringhe da formattare
 * @param or - Se true usa disgiunzione ('o'), altrimenti congiunzione ('e') (default: false)
 * @returns Lista formattata con separatori localizzati
 */
export function formatList(values: Iterable<string>, or = false) {
  const fmt = or ? disjunctionFormatter : conjunctionFormatter;
  return fmt.format(values);
}

/**
 * Crea una Promise che si risolve dopo il tempo specificato (utility per async/await delay)
 * @param ms - Millisecondi di attesa
 * @returns Promise che si risolve dopo il tempo specificato
 */
export function sleep(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Converte una stringa base64 URL-safe in Uint8Array (utile per Push Notifications)
 * @param base64String - Stringa base64 con encoding URL-safe
 * @returns Array di byte decodificato
 */
export function urlBase64ToUint8Array(base64String: string) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');

  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Codifica File/Blob in oggetto serializzabile con contenuto base64 per invio al backend
 * @param v - Valore da codificare (File, Blob, o altro)
 * @returns Oggetto con {name, size, type, content} se File/Blob, altrimenti il valore originale
 */
export async function encodeForSend(v: any) {
  if (v instanceof File || v instanceof Blob) {
    const buffer = new Uint8Array(await v.arrayBuffer());
    const content = btoa(buffer.reduce((p, c) => p + String.fromCharCode(c), ''));
    return {
      name: v instanceof File ? v.name : null,
      size: v.size,
      type: v.type,
      content,
    };
  } else {
    return v;
  }
}

/**
 * Crea un oggetto combinando array di chiavi e valori (simile a lodash.zipObject)
 * @template K - Tipo delle chiavi (array readonly di stringhe)
 * @template V - Tipo dei valori
 * @param keys - Array di stringhe da usare come chiavi
 * @param values - Array o oggetto con i valori corrispondenti
 * @returns Oggetto con chiavi mappate ai valori
 */
export function zipObject<K extends readonly string[], V extends Record<K[number], any>>(keys: K, values: V): Record<K[number], V[K[number]]>;
export function zipObject<K extends readonly string[], V extends any[]>(keys: K, values: V): Record<K[number], V>;
export function zipObject(keys: string[], values: any[] | Record<string, any>): Record<string, any> {
  return keys.reduce((accumulator: Record<string, any>, key, index) => {
    accumulator[key] = Array.isArray(values) ? values[index] : values[key];
    return accumulator;
  }, {});
}

/**
 * Verifica se un valore è un oggetto plain (non una classe, array, ecc.)
 * @param obj - Valore da verificare
 * @returns true se è un oggetto plain (literal o Object.create(null))
 */
export function isPlainObject(obj: any): boolean {
  const prototype = Object.getPrototypeOf(obj);
  return prototype === Object.getPrototypeOf({}) || prototype === null;
}

/**
 * Risolve ricorsivamente tutte le Promise contenute in un oggetto o array (deep Promise.all)
 * @param obj - Oggetto o array contenente potenzialmente Promise annidate
 * @returns Promise che si risolve con la struttura completa con tutte le Promise risolte
 */
export async function recursiveObjectPromiseAll(obj: any[] | Record<string, any>): Promise<any[] | Record<string, any>> {
  if (Array.isArray(obj)) {
    return await Promise.all(obj.map(v => (typeof v === 'object' && !v.then && isPlainObject(v) ? recursiveObjectPromiseAll(v) : v)));
  }
  const keys = Object.keys(obj);
  const result = await Promise.all(
    keys.map(key => {
      const value = obj[key];
      // Promise.resolve(value) !== value should work, but !value.then always works
      if ((typeof value === 'object' && !value.then && isPlainObject(value)) || Array.isArray(value)) {
        return recursiveObjectPromiseAll(value);
      }
      return value;
    }),
  );
  return zipObject(keys, result);
}

/**
 * Calcola la posizione del mouse relativa al layer/elemento (compensando scroll e offset)
 * @param evt - Evento mouse da cui estrarre le coordinate
 * @returns Oggetto con coordinate {x, y} relative al layer, o undefined se il target non è HTMLElement
 */
export function getLayerOffset(evt: MouseEvent) {
  if (!(evt.target instanceof HTMLElement)) {
    return;
  }

  let el = evt.target;
  let x = 0;
  let y = 0;

  while (el && !isNaN(el.offsetLeft) && !isNaN(el.offsetTop) && el.offsetParent instanceof HTMLElement) {
    x += el.offsetLeft - el.scrollLeft;
    y += el.offsetTop - el.scrollTop;
    el = el.offsetParent;
  }

  x = evt.clientX - x;
  y = evt.clientY - y;

  return { x: x, y: y };
}

/**
 * Estrae una chiave valida (string|number|symbol) da un valore, o restituisce un fallback
 * @param value - Valore da cui estrarre la chiave
 * @param fallback - Valore da restituire se value non è una chiave valida
 * @returns Chiave valida o fallback
 */
export function keyFor(value: unknown, fallback: string | number | symbol): string | number | symbol {
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'symbol') {
    return value;
  }
  return fallback;
}

/**
 * Mescola in-place un array usando l'algoritmo Fisher-Yates
 * @template V - Tipo degli elementi dell'array
 * @template T - Tipo dell'array
 * @param a - Array da mescolare (modificato in-place)
 * @returns L'array mescolato (stesso riferimento dell'input)
 */
export function shuffle<V, T extends Array<V>>(a: T): T {
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j] as V, a[i] as V];
  }
  return a;
}

/**
 * Seleziona un elemento casuale da un array
 * @template T - Tipo dell'array
 * @param a - Array da cui selezionare
 * @returns Elemento casuale dell'array
 */
export function pickRandom<T extends any[]>(a: T): ArrayElement<T> {
  const r = Math.floor(Math.random() * a.length);
  return a[r];
}

/**
 * Inietta dinamicamente un tag script nel DOM e attende il caricamento
 * @param src - URL dello script da caricare
 * @param target - Elemento HTML in cui inserire lo script (default: document.head)
 * @returns Promise che si risolve quando lo script è caricato, o rejetta in caso di errore
 */
export function injectScript(src: string, target: HTMLElement = document.head): Promise<void> {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.async = true;
    script.src = src;
    script.addEventListener('load', () => resolve());
    script.addEventListener('error', () => {
      reject(new Error(`Failed to load ${src}`));
    });
    target.appendChild(script);
  });
}

/**
 * Copia una stringa negli appunti del sistema usando l'API Clipboard
 * @param firma - Testo da copiare negli appunti
 */
export function copyToClipBoard(firma: string) {
  navigator.clipboard.writeText(firma);
}

/**
 * Estrae un singolo elemento da un array o restituisce il valore se non è un array
 * @template T - Tipo dell'elemento
 * @param items - Singolo elemento o array
 * @returns Ultimo elemento dell'array (via pop) o il valore stesso se non è array
 */
export function pop<T>(items: T | T[]): T | undefined {
  return Array.isArray(items) ? items.pop() : items;
}

/**
 * Funzione di cast generico che restituisce il valore senza modifiche, ma con tipo specificato (utile per bypassare errori di tipo o per chiarezza)
 */
export function cast<T>(v: any): T {
  return v;
}

/**
 * Triggera il download di un Blob nel browser con nome file specificato
 * @param data - Blob da scaricare
 * @param filename - Nome del file da salvare
 */
export function saveAs(data: Blob, filename: string) {
  const link = document.createElement('a');
  link.download = filename;
  link.href = URL.createObjectURL(data);
  link.dispatchEvent(new MouseEvent('click'));
}

/** Tipo per valori booleani in attributi HTML (true, false, '', undefined) */
export type Booleanish = InputHTMLAttributes['required'];

/**
 * Converte un valore Booleanish (attributo HTML) in booleano effettivo
 * @param b - Valore Booleanish da convertire
 * @returns true se il valore rappresenta true (inclusa stringa vuota), false altrimenti
 */
export function booleanish(b?: Booleanish | '') {
  return b === '' || (Boolean(b) && b !== 'false');
}

// REGION
// I primi due caratteri dell'indice devono rappresentare l'ISO di uno stato per mostrarne la bandiera

export const locations: Record<(typeof REGION)[number], string> = {
  ch: 'Svizzera',
  eu: 'Unione Europea',
};

/**
 * Esporta un array di righe in formato CSV con i campi specificati
 * @param fields - Array dei nomi dei campi da includere nel CSV
 * @param rows - Array di righe da esportare
 * @param filename - Nome del file CSV da generare
 */
export function saveCSV(fields: string[], rows: Record<string, any>[], filename: string) {
  const csv = [fields.map(f => f.toUpperCase()).join(';')];

  for (const row of rows) {
    const csvrow = [];
    for (const f of fields) {
      let value = row[f];

      if (value === null) {
        csvrow.push('');
        continue;
      }

      const typ = typeof value;
      if (typ == 'string') {
        value = `"${value.split('"').join('""')}"`;
      } else if (Array.isArray(value)) {
        value = `"${value.join(', ').split('"').join('""')}"`;
      } else if (value instanceof Date || value instanceof DateTime) {
        value = `"${dateFormat(value, 'dd/MM/yyyy').split('"').join('""')}"`;
      } else if (typ == 'number') {
        value = parseFloat(value).toLocaleString();
      } else {
        console.log(f, typ, value);
      }

      csvrow.push(value);
    }
    csv.push(csvrow.join(';'));
  }

  const blob = new Blob([csv.join('\r\n')], {
    type: 'text/csv;charset=utf-8',
  });
  saveAs(blob, filename);
}
